import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import { DeviceModel } from '../../core/models/account/device.model';
import { LoginModel } from '../../core/models/account/login.model';
import { AccountService } from '../../core/services/api/account.service';
import { StorageService } from '../../core/services/storage.service';

@Component({
    selector: 'app-relogin-modal',
    templateUrl: './relogin-modal.component.html',
    styleUrls: ['./relogin-modal.component.scss'],
    standalone: false
})
export class ReloginModalComponent implements OnInit {
  @Input() email: string;

  form: UntypedFormGroup;
  submitting: boolean;
  errorMessage: string;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly storageService: StorageService,
    public readonly activeModal: NgbActiveModal,
  ) {}

  ngOnInit() {
    this.email = this.email || this.storageService.email;

    // Initialize the form unconditionally — the template's [formGroup]="form" binding runs
    // change detection at least once before a dismiss() takes effect, and ReactiveForms throws
    // if `form` isn't a FormGroup instance yet.
    this.form = this.formBuilder.group({
      password: ['', Validators.required],
    });

    // No email to re-authenticate with (e.g. localStorage was cleared) — submit() would
    // otherwise crash on `this.email.trim()`. Dismiss instead; the caller (HttpResponseInterceptor)
    // already treats a dismissed modal the same as a failed re-auth and redirects to login.
    if (!this.email) {
      this.activeModal.dismiss();
    }
  }

  submit() {
    if (this.form.invalid || this.submitting) {
      return;
    }

    this.errorMessage = null;
    this.submitting = true;

    const model: LoginModel = {
      Email: this.email.trim(),
      Password: this.form.value.password.trim(),
      Device: new DeviceModel(this.storageService.deviceId, this.storageService.registrationId),
    };

    this.accountService
      .login(model)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => this.activeModal.close(),
        error: (err) => {
          // 2FA can't be completed inline here — bail out to the normal login page fallback
          // rather than reimplementing the OTP step inside this modal.
          if (err?.code === 'ERR_REQ_2FA_CODE') {
            this.activeModal.dismiss();
            return;
          }

          this.errorMessage = err?.message || 'login.reloginModal.genericError';
        },
      });
  }

  cancel() {
    this.activeModal.dismiss();
  }
}
